import React, { useState } from "react";
import { 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Typography, 
  Tooltip, 
  Paper, 
  Card, 
  IconButton,
  Chip,
  Button,
  Collapse
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  TableChart,
  CheckCircle,
  Info
} from '@mui/icons-material';
import RadioButton from "../../components/RadioButton";
import "../../css/OutPutDirectory.css";
import { useSelector } from "react-redux";
import { FORM_KEYS } from "../../utills/constant";
import { motion } from "framer-motion";

const OutputReview = () => {
  const csvData = useSelector((state) => state.CSVDataSlice.csvData);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const displayData = isExpanded ? csvData : (csvData ? csvData.slice(0, 5) : []);

  return (
    <Card sx={{ 
      width: '100%',
      maxWidth: '100%',
      borderRadius: 3,
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid rgba(0,0,0,0.05)',
      overflow: 'hidden',
      backgroundColor: '#ffffff'
    }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: { xs: 2, md: 3 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: 'white',
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{
            width: { xs: 40, md: 48 },
            height: { xs: 40, md: 48 },
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
            flexShrink: 0
          }}>
            <TableChart sx={{ fontSize: { xs: 20, md: 24 } }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ 
              fontWeight: 700, 
              mb: 0.5,
              fontSize: { xs: '1.1rem', md: '1.25rem' }
            }}>
              Output Fields
            </Typography>
            <Typography variant="body2" sx={{ 
              opacity: 0.9,
              fontSize: { xs: '0.8rem', md: '0.9rem' }
            }}>
              {csvData?.length || 0} fields configured for extraction
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`${csvData?.length || 0} Fields`}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 600,
              fontSize: { xs: '0.75rem', md: '0.85rem' },
              backdropFilter: 'blur(10px)',
              flexShrink: 0
            }}
          />
          <Button
            variant="contained"
            onClick={handleToggleExpand}
            startIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              textTransform: 'none',
              borderRadius: 2,
              px: { xs: 1.5, md: 2 },
              py: { xs: 0.75, md: 1 },
              fontWeight: 600,
              backdropFilter: 'blur(10px)',
              fontSize: { xs: '0.8rem', md: '0.9rem' },
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.3)',
              },
              flexShrink: 0
            }}
          >
            {isExpanded ? 'Show Less' : 'Show More'}
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: { xs: 2, md: 3 }, overflowX: 'hidden' }}>
        {csvData && csvData.length > 0 ? (
          <>
            <TableContainer 
              component={Paper} 
              sx={{ 
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                maxHeight: isExpanded ? 'none' : '400px',
                width: '100%',
                maxWidth: '100%'
              }}
            >
              <Table size="medium" sx={{ minWidth: { xs: 300, sm: 400 } }}>
                <TableHead>
                  <TableRow sx={{ 
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    '& th': {
                      borderBottom: '2px solid #dee2e6',
                      fontWeight: 700,
                      color: '#495057',
                      fontSize: { xs: '0.8rem', md: '0.9rem' },
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      py: { xs: 1.5, md: 2 },
                      px: { xs: 1, md: 2 },
                      whiteSpace: 'nowrap'
                    }
                  }}>
                    <TableCell sx={{ width: '60px' }}>ID</TableCell>
                    <TableCell>Field Key</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="center" sx={{ width: '100px' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayData.map((item, index) => (
                    <TableRow 
                      key={index} 
                      hover 
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: '#f8f9fa',
                        },
                        '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <TableCell sx={{ py: { xs: 1.5, md: 2 }, px: { xs: 1, md: 2 } }}>
                        <Box sx={{
                          width: { xs: 28, md: 32 },
                          height: { xs: 28, md: 32 },
                          borderRadius: '50%',
                          backgroundColor: '#e3f2fd',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          color: '#1976d2',
                          fontSize: { xs: '0.75rem', md: '0.85rem' }
                        }}>
                          {index + 1}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: { xs: 1.5, md: 2 }, px: { xs: 1, md: 2 } }}>
                        <Typography sx={{ 
                          fontWeight: 600,
                          color: '#1a202c',
                          fontSize: { xs: '0.8rem', md: '0.9rem' },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: { xs: '120px', sm: '150px', md: '200px' }
                        }}>
                          {item.Keys}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: { xs: 1.5, md: 2 }, px: { xs: 1, md: 2 } }}>
                        <Typography sx={{ 
                          color: '#4a5568',
                          fontSize: { xs: '0.75rem', md: '0.85rem' },
                          lineHeight: 1.4,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          maxWidth: { xs: '150px', sm: '200px', md: '250px' }
                        }}>
                          {item.Description}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: { xs: 1.5, md: 2 }, px: { xs: 1, md: 2 } }} align="center">
                        <Chip
                          label="Active"
                          size="small"
                          sx={{
                            backgroundColor: '#e8f5e8',
                            color: '#2e7d32',
                            fontWeight: 600,
                            fontSize: { xs: '0.65rem', md: '0.75rem' },
                            height: { xs: '20px', md: '24px' }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Expand/Collapse Indicator */}
            {csvData.length > 5 && (
              <Box sx={{ 
                mt: 2, 
                textAlign: 'center',
                p: { xs: 1.5, md: 2 },
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Typography variant="body2" sx={{ 
                  color: '#6c757d',
                  fontSize: { xs: '0.75rem', md: '0.85rem' },
                  fontWeight: 500
                }}>
                  {isExpanded 
                    ? `Showing all ${csvData.length} fields` 
                    : `Showing first 5 of ${csvData.length} fields`
                  }
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ 
            textAlign: 'center', 
            py: { xs: 4, md: 6 },
            px: { xs: 2, md: 3 }
          }}>
            <Box sx={{
              width: { xs: 60, md: 80 },
              height: { xs: 60, md: 80 },
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <Info sx={{ fontSize: { xs: 30, md: 40 }, color: '#adb5bd' }} />
            </Box>
            <Typography variant="h6" sx={{ 
              color: '#495057', 
              mb: 1,
              fontWeight: 600,
              fontSize: { xs: '1rem', md: '1.25rem' }
            }}>
              No Output Fields
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#6c757d',
              maxWidth: 300,
              margin: '0 auto',
              fontSize: { xs: '0.8rem', md: '0.9rem' }
            }}>
              Configure output fields in the Fields tab to see them here
            </Typography>
          </Box>
        )}
      </Box>

      {/* Summary Footer */}
      {csvData && csvData.length > 0 && (
        <Box sx={{
          p: { xs: 2, md: 3 },
          borderTop: '1px solid #e9ecef',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{
              width: { xs: 32, md: 36 },
              height: { xs: 32, md: 36 },
              borderRadius: '50%',
              backgroundColor: '#10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <CheckCircle sx={{ color: 'white', fontSize: { xs: 16, md: 18 } }} />
            </Box>
            <Box>
              <Typography variant="body1" sx={{ 
                fontWeight: 600, 
                color: '#1a202c',
                fontSize: { xs: '0.85rem', md: '0.95rem' }
              }}>
                Fields Configured
              </Typography>
              <Typography variant="body2" sx={{ 
                color: '#64748b',
                fontSize: { xs: '0.75rem', md: '0.8rem' }
              }}>
                Ready for document processing
              </Typography>
            </Box>
          </Box>
          <Chip 
            label={`${csvData.length} Active Fields`}
            sx={{
              backgroundColor: '#10b981',
              color: 'white',
              fontWeight: 600,
              fontSize: { xs: '0.75rem', md: '0.8rem' },
              flexShrink: 0
            }}
          />
        </Box>
      )}
    </Card>
  );
};

export default OutputReview;
